import React, { useEffect, useState } from 'react';
import { Button, Text, TextInput, View } from "../../Components/Themed";
import { FlatList, Image, type ViewStyle } from 'react-native';
import type { CreateGroupStepProps, FocusPositionProps, GroupPlayerProps, GroupProps, MyPlayerProps, PublicPlayerProps } from '../../types';
import { Icons } from '../../Components';
import { useColors } from '../../constants/useColors';
import { GroupApi, GroupHelpers } from '../api';
import ImageUploader from '../../Components/ImageUploader';
import { showConfirmAlert } from '../../Components/ConfirmAlert';

type CreateGroupWizardProps = {
    player:MyPlayerProps,
    refresh_key:string,
    header_style?:ViewStyle,
    footer_style?:ViewStyle,
    onInvitePlayers:() => void,
    onFocusPosition?:(pos:FocusPositionProps) => void,
    onCreatedGroup:(group:GroupProps) => void,
    onRequestAuthenticate:() => void,
    onClose:() => void,
    onFinish:(group:GroupProps, group_players:GroupPlayerProps[]) => void
}



const wizard_steps:CreateGroupStepProps[] = [
    { num: 1, step_key:'intro', title: 'BettorEdge Groups', description: 'Groups bring your group chat, discord server, family banter to real life!  Track your friends stats, compete against each other and have fun!' },
    { num: 2, step_key:'info', title: 'Describe Your Group', description: 'Give this group a name and a description that is unique and will entice players and friends to join!' },
    { num: 3, step_key: 'image', title: 'Add a fun photo', description: 'Add an image so its easy to recognize your group!' },
    { num: 4, step_key: 'invite', title: 'Invite Friends!', description: 'Groups are of no use without friends to join!  Have a group text, slack, discord? Add them to start competing and smack talking!' },
    { num: 5, step_key: 'success', title: 'Group Successfully Created!', description: 'Your group has been created successfully! Activate it now and start playing with friends.' }
]
const sections = ['header', 'intro', 'info', 'input']
const CreateGroupWizard = ({ player, refresh_key, header_style, footer_style, onCreatedGroup, onFocusPosition, onRequestAuthenticate, onClose, onInvitePlayers, onFinish }:CreateGroupWizardProps) => {
    const Colors = useColors();
    const [ wiz_size, setSize ] = useState({ height: 0, width:0 })
    const [ wiz_state, setState ] = useState<{
        loading:boolean,
        active_step: CreateGroupStepProps,
        draft_group:GroupProps,
        group_players:GroupPlayerProps[],
        players:PublicPlayerProps[]
    }>({
        loading:false,
        draft_group: GroupHelpers.generateGroup(),
        group_players: [],
        players:[],
        active_step: wizard_steps[0] as CreateGroupStepProps
    });
    const { active_step, loading, draft_group, group_players, players } = wiz_state;
    const errors = GroupHelpers.isStepValid(active_step, draft_group);

    useEffect(() => {
        GroupApi.setEnvironment();
        refreshData();
    },[refresh_key]);

    const refreshData = async() => {
        if(!draft_group.group_id){ return } //Group hasnt been created yet!
        setState({ ...wiz_state, loading: true });
        const resp = await GroupApi.getGroupById(draft_group.group_id)
        if(!resp){ 
            return setState({ 
                ...wiz_state,
                loading:false 
            })
        }
        //Lets grab the players!
        let player_ids = resp.group_players.map(p => p.player_id);
        let plyers = await GroupApi.getPlayersByIds(player_ids);
        setState({ 
            ...wiz_state,
            draft_group: resp.group,
            players: plyers,
            group_players: resp.group_players,
            loading:false 
        });
    }

    const handleDeleteAndClose = async() => {
        setState({ ...wiz_state, loading: true });
        //If we created a group but are closing it - we need to delete the group
        let deleted_group = await GroupApi.deleteGroup(draft_group.group_id);
        if(!deleted_group){ alert('Group was created.  You can manage this group from your social tab') }
        //Reset state on close!
        setState({ 
            ...wiz_state, 
            loading: false,
            draft_group: GroupHelpers.generateGroup(),
            group_players: [],
            players:[],
        });
        return onClose()
    }

    const handleClose = async() => {
        if(!draft_group.group_id){ return onClose() }
        showConfirmAlert('Are you sure?', 'Exiting now will delete the group in progress',
            async() => await handleDeleteAndClose(),
            () => console.log('')
        )
    }


    const handleFinishStep = async(step:CreateGroupStepProps) => {
        if(errors.length > 0){ return }
        switch(step.step_key){
            case 'intro':
                if(!player){ return onRequestAuthenticate() }
                let first_step = wizard_steps.find(s => s.num == step.num + 1);
                if(!first_step){ return }
                setState({
                    ...wiz_state,
                    loading: false,
                    active_step: first_step,
                });
                return
            case 'info':
                setState({ ...wiz_state, loading: true });
                let new_group:GroupProps | undefined = undefined
                if(draft_group.group_id){
                    new_group = await GroupApi.updateGroup(draft_group);
                } else {
                    new_group = await GroupApi.createGroup(draft_group.name, draft_group.description, draft_group.invite_code);
                }
                if(!new_group){
                    alert('Unable to create group. Please try again later.')
                    return setState({ ...wiz_state, loading: false })
                }
                onCreatedGroup(new_group);
                let next_step = wizard_steps.find(s => s.num == step.num + 1);
                if(!next_step){ return }
                setState({
                    ...wiz_state,
                    loading: false,
                    active_step: next_step,
                    draft_group: new_group
                });
                return
            case 'image':
                if(!draft_group.group_id){ return alert('Unable to process') }
                setState({ ...wiz_state, loading: true });
                
                let image_group = await GroupApi.updateGroup(draft_group);
                if(!image_group){
                    alert('Unable to create group. Please try again later.')
                    return setState({ ...wiz_state, loading: false })
                }
                let next_image_step = wizard_steps.find(s => s.num == step.num + 1);
                if(!next_image_step){ return }
                setState({
                    ...wiz_state,
                    loading: false,
                    active_step: next_image_step,
                    draft_group: image_group
                });
                return
            case 'invite':
                let success_step = wizard_steps.find(s => s.num == step.num + 1);
                if(!success_step){ return }
                setState({
                    ...wiz_state,
                    loading: false,
                    active_step: success_step,
                });
                return
            case 'success':
                if(!draft_group.group_id){ return alert('Unable to activate group.  Please try again') }
                setState({ ...wiz_state, loading: true });
                let activated_group = await GroupApi.activateGroup(draft_group.group_id);
                if(!activated_group){
                    alert('Unable to activate group. Please try again later.')
                    return setState({ ...wiz_state, loading: false })
                }
                setState({
                    ...wiz_state,
                    loading: false,
                    draft_group: activated_group
                });
                onFinish(activated_group, group_players);
                return
            default: return
        }
    }

    const renderPlayers = (data:{ item:GroupPlayerProps, index:number }) => {
        const pl = players.find(p => p.player_id == data.item.player_id);
        if(!pl){ return <></> }
        return (
            <View key={data.item.group_player_id.toString()} float style={{  width:100, margin:5 }}>
                <Image
                    source={{ uri: pl.profile_pic }}
                    style={{ height:50, width:100, borderTopLeftRadius:8, borderTopRightRadius: 8 }}
                    resizeMode='cover'
                />
                <View transparent style={{ padding:10 }}>
                    <Text theme='h1' textAlign='center'>@{pl.username}</Text>
                </View>
            </View>
        )
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, ...header_style }}>
                        <View transparent style={{ flex:1 }}>
                            <Text theme='h1'>Create Group Wizard</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Complete the steps to create your group!</Text>
                        </View>
                        <Button
                            title='X'
                            float
                            title_color={Colors.text.error}
                            style={{ padding:10 }}
                            onPress={() => handleClose()}
                        />
                    </View>
                )
            case 'intro':
                if(active_step?.step_key != 'intro'){ return <></> }
                return (
                    <View>
                        <Image
                            source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/w_1000,ar_16:9,c_fill,g_auto,e_sharpen/v1756998145/ChatGPT_Image_Sep_4_2025_10_00_58_AM_xemjix.png' }}
                            style={{ alignSelf:'center', width: wiz_size.width > 600 ? 600 : wiz_size.width, height: wiz_size.width > 600 ? 300 : wiz_size.width * 0.5 }}
                            resizeMode='cover'
                        />
                    </View>
                )
            case 'info':
                if(!active_step){ return <></> }
                return (
                    <View style={{ padding:20 }}>
                        <Text theme='h1' size={40}>{active_step.title}</Text>
                        <Text theme='description' size={18} color={errors.length > 0 ? Colors.text.warning : Colors.text.success} style={{ marginTop:10 }}>{active_step.description}</Text>
                    </View>
                )
            case 'input':
                if(!active_step){ return <></> }
                switch(active_step.step_key){
                    case 'info':
                        return (
                            <View style={{ padding:10 }}>
                                <Text theme='h1' style={{ marginLeft:10 }}>Group Name</Text>
                                <TextInput
                                    value={draft_group.name}
                                    onFocusPosition={onFocusPosition}
                                    style={{ margin:10, borderColor:draft_group.name?Colors.text.success:Colors.text.warning }}
                                    placeholder='Give your group a name ...'
                                    onChangeText={(text) => setState({ ...wiz_state, draft_group: { ...draft_group, name: text } })}
                                />
                                <Text theme='h1' style={{ marginLeft:10 }}>Group Description</Text>
                                <TextInput
                                    value={draft_group.description}
                                    onFocusPosition={onFocusPosition}
                                    style={{ margin:10, borderColor:draft_group.description?Colors.text.success:Colors.text.warning }}
                                    placeholder='Add a short description ...'
                                    onChangeText={(text) => setState({ ...wiz_state, draft_group: { ...draft_group, description: text } })}
                                />
                                <Text theme='h1' style={{ marginLeft:10 }}>Invite Code</Text>
                                <TextInput
                                    value={draft_group.invite_code}
                                    onFocusPosition={onFocusPosition}
                                    style={{ margin:10, borderColor:draft_group.invite_code?Colors.text.success:Colors.text.warning }}
                                    placeholder='Add a unique code to find your group'
                                    onChangeText={(text) => setState({ ...wiz_state, draft_group: { ...draft_group, invite_code: text } })}
                                />
                            </View>
                        )
                    case 'image':
                        return (
                            <View style={{ padding:10, justifyContent:'center', alignItems:'center' }}>
                                <ImageUploader public_id={`${draft_group.group_id}_${Math.random()}`} onFinishUpload={(obj) => setState({ ...wiz_state, draft_group: { ...draft_group, group_image: { ...obj, url: obj.secure_url } } })}>
                                    {draft_group.group_image?.url ?
                                    <Image
                                        source={{ uri: draft_group.group_image.url }}
                                        style={{ height:wiz_size.width >600 ? 600 : wiz_size.width, width:wiz_size.width >600 ? 600 : wiz_size.width }}
                                        resizeMode='contain'
                                    />
                                    :
                                    <Icons.ImageIcon size={150} color={Colors.text.h1} />
                                    }
                                    <Text style={{ marginTop:10 }} theme='action' textAlign='center' size={18}>{draft_group.group_image?.url ? 'CHANGE':'ADD'} PHOTO</Text>
                                </ImageUploader>
                            </View>
                        )
                    case 'invite':
                        return (
                            <View style={{ padding:10 }}>
                                <Button
                                    title={group_players.length == 0 ? 'INVITE FRIENDS' : 'INVITE MORE'}
                                    type='success'
                                    onPress={() => onInvitePlayers()}
                                />
                                <View style={{ margin:10, marginTop:40 }}>
                                    <View style={{ flexDirection:'row', flexWrap:'wrap', justifyContent:'center' }}>
                                        {group_players.filter(gp => gp.status != 'inactive').map((item, index) => {
                                            return renderPlayers({ item, index })
                                        })}
                                    </View>
                                </View>
                            </View>
                        )
                    case 'success':
                        return (
                            <View>
                                <Image
                                    source={{ uri:'https://res.cloudinary.com/hoabts6mc/image/upload/v1714410165/Welcome_banner_bqu6cc.webp' }}
                                    style={{ alignSelf:'center', width:wiz_size.width > 500 ? 500 :wiz_size.width, height:wiz_size.width > 500 ? 500 :wiz_size.width, borderRadius:4 }}
                                    resizeMode='cover'
                                />
                            </View>
                        )
                    default: return <></>
                }
            default: return <></>
        }
    }

    return (
        <View transparent style={{ flex:1 }}>
            <View transparent style={{ flex:1 }} onLayout={(ev) => {
                const { height, width } = ev.nativeEvent.layout;
                setSize({ height, width })
            }}>
                <FlatList
                    data={sections}
                    key={'create_group_steps'}
                    keyExtractor={item => item}
                    renderItem={renderSections}
                />
            </View>
            {active_step.step_key == 'intro' ?
            <View type='footer' style={{ flexDirection: 'row', alignItems:'center', padding:10, ...footer_style }}>
               <Button 
                    style={{ flex:1 }}
                    title='GET STARTED!'
                    type='success'
                    onPress={() => handleFinishStep(active_step)}
                />
            </View>
            :
            <View type='footer' style={{ flexDirection: 'row', alignItems:'center', padding:10, ...footer_style }}>
                <Button 
                    disabled={loading || active_step?.num == 1 ? true : false} 
                    float 
                    onPress={() => {
                        let prev_step = wizard_steps.find(s => s.num == active_step?.num - 1)
                        if(!prev_step){ return }
                        setState({ ...wiz_state, active_step: prev_step })
                    }}
                    style={{ borderRadius:100, padding:0, height:50, width:50, justifyContent:'center', alignItems:'center', opacity: active_step?.num == 1 ? 0.5 : 1 }}>
                    <Icons.ChevronIcon direction='left' size={14} color={Colors.text.h1} />
                </Button>
                <View transparent style={{ flex:1 }}>
                    <Text theme='h1' textAlign='center'>Step {active_step.num} of {wizard_steps.length}</Text>
                </View>
                {active_step?.step_key == 'success' ?
                <Button
                    type='success'
                    title='ACTIVATE'
                    onPress={() => handleFinishStep(active_step)}
                />
                :
                <Button 
                    float
                    disabled={loading ? true : false}
                    loading={loading}
                    style={{ borderRadius:100, padding:0, height:50, width:50, justifyContent:'center', alignItems:'center', opacity: errors.length > 0 || loading ? 0.5: 1 }}
                    onPress={() => {
                        if(errors.length > 0){ return alert(errors.map(e => e)) }
                        handleFinishStep(active_step)
                    }}
                >
                    <Icons.ChevronIcon direction='right' size={14} color={errors.length > 0 ? Colors.text.h1: Colors.text.success} />
                </Button>
                }
            </View>
            }
        </View>
    )
}

export default CreateGroupWizard